import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Upload, AlertCircle, FileText, Image, File } from "lucide-react";

export type Attachment = {
  file: File;
  description: string;
};

interface FileUploaderProps {
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
  maxFileSize?: number;
  allowedTypes?: string[];
  label?: string;
  required?: boolean;
  showFileSize?: boolean;
  maxFiles?: number;
}

export function FileUploader({
  attachments,
  onAttachmentsChange,
  maxFileSize = 10,
  allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/gif",
    "image/webp",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "text/csv"
  ],
  label = "Adjuntos",
  required = false,
  showFileSize = true,
  maxFiles
}: FileUploaderProps) {
  const [error, setError] = useState<string>("");

  const validateFile = (file: File): string | null => {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      return `El archivo "${file.name}" excede el tamaño máximo de ${maxFileSize}MB`;
    }

    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      return `El tipo de archivo "${file.name}" no está permitido`;
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    if (!e.target.files) return;

    const filesArray = Array.from(e.target.files);
    
    if (maxFiles && attachments.length + filesArray.length > maxFiles) {
      setError(`Solo puedes subir un máximo de ${maxFiles} archivo(s)`);
      e.target.value = "";
      return;
    }

    const validFiles: Attachment[] = [];
    const errors: string[] = [];

    filesArray.forEach(file => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        validFiles.push({ file, description: "" });
      }
    });

    if (errors.length > 0) {
      setError(errors.join(". "));
    }

    if (validFiles.length > 0) {
      onAttachmentsChange([...attachments, ...validFiles]);
    }

    e.target.value = "";
  };

  const removeAttachment = (index: number) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== index));
    setError("");
  };

  const updateDescription = (index: number, description: string) => {
    const updated = attachments.map((att, i) =>
      i === index ? { ...att, description } : att
    );
    onAttachmentsChange(updated);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <Image className="h-4 w-4 text-blue-600" />;
    }
    if (fileType === "application/pdf") {
      return <FileText className="h-4 w-4 text-red-600" />;
    }
    return <File className="h-4 w-4 text-slate-600" />;
  };

  const getFileTypeLabel = (): string => {
    if (allowedTypes.length === 0) return "Todos los tipos";
    
    const types = new Set<string>();
    allowedTypes.forEach(type => {
      if (type.includes("pdf")) types.add("PDF");
      if (type.includes("image")) types.add("Imágenes");
      if (type.includes("word") || type.includes("document")) types.add("Word");
      if (type.includes("excel") || type.includes("sheet")) types.add("Excel");
      if (type.includes("text")) types.add("Texto");
      if (type.includes("csv")) types.add("CSV");
    });
    
    return Array.from(types).join(", ");
  };

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      <div className="flex items-center space-x-4">
        <input
          id="file-upload-input"
          type="file"
          multiple={!maxFiles || maxFiles > 1}
          onChange={handleFileChange}
          className="hidden"
          accept={allowedTypes.join(",")}
        />
        <label htmlFor="file-upload-input">
          <Button
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            asChild
          >
            <span>
              <Upload className="h-4 w-4 mr-2" />
              Elegir archivos
            </span>
          </Button>
        </label>
        {attachments.length > 0 && (
          <span className="text-sm text-slate-600">
            {attachments.length} archivo(s) seleccionado(s)
            {maxFiles && ` (máx. ${maxFiles})`}
          </span>
        )}
      </div>

      {error && (
        <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Tamaño máximo: {maxFileSize}MB. Tipos permitidos: {getFileTypeLabel()}
      </p>

      {attachments.length > 0 && (
        <div className="mt-3 space-y-2 border rounded-md p-3 bg-slate-50/50">
          {attachments.map((att, idx) => (
            <div
              key={idx}
              className="flex flex-col md:flex-row md:items-center gap-2 p-3 bg-white rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {getFileIcon(att.file.type)}
                  <span className="text-sm font-medium truncate">
                    {att.file.name}
                  </span>
                  {showFileSize && (
                    <span className="text-xs text-slate-500 flex-shrink-0">
                      ({formatFileSize(att.file.size)})
                    </span>
                  )}
                </div>
              </div>
              <Input
                placeholder="Descripción del archivo (opcional)"
                value={att.description}
                onChange={(e) => updateDescription(idx, e.target.value)}
                className="md:flex-1 md:max-w-md"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                onClick={() => removeAttachment(idx)}
                title="Eliminar archivo"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}